import { TextInput } from "@jugl-web/ui-kit";
import {
  CountryCode,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "libphonenumber-js";
import { FC, HTMLInputTypeAttribute } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { NativeCountryCodeSelect } from "./NativeCountryCodeSelect";

interface FormFieldValue {
  value: string;
  countryCode?: CountryCode;
}

interface FormValues {
  fields: FormFieldValue[];
}

interface ChatBotPhoneInputProps {
  inputDef: {
    name: string;
    type: HTMLInputTypeAttribute;
    isRequired?: boolean;
  };
  fieldIndex: number;
  control: Control<FormValues>;
  defaultCountryCode: CountryCode;
  errorMessage?: string;
}

export const ChatBotPhoneInput: FC<ChatBotPhoneInputProps> = ({
  inputDef,
  fieldIndex,
  control,
  defaultCountryCode,
  errorMessage,
}) => {
  const countryCode =
    useWatch({
      control,
      name: `fields.${fieldIndex}.countryCode`,
    }) ?? defaultCountryCode;

  const valueFieldName = `fields.${fieldIndex}.value` as const;
  const countryCodeFieldName = `fields.${fieldIndex}.countryCode` as const;

  return (
    <Controller
      name={valueFieldName}
      control={control}
      rules={{
        validate: (value: string) => {
          if (inputDef.isRequired && value.trim() === "") {
            return "This field is required";
          }

          if (!value) {
            return true;
          }

          const fullNumber = `+${getCountryCallingCode(countryCode)}${value}`;

          return isValidPhoneNumber(fullNumber) || "Invalid phone number";
        },
      }}
      render={({ field }) => (
        <TextInput
          type="tel"
          inputMode="numeric"
          isRequired={inputDef.isRequired}
          labelPosition="border-overlap"
          size="default"
          startContent={
            <Controller
              name={countryCodeFieldName}
              control={control}
              defaultValue={defaultCountryCode}
              render={({ field: countryField }) => (
                <NativeCountryCodeSelect
                  value={countryField.value ?? defaultCountryCode}
                  suggestedCountryCode={defaultCountryCode}
                  onChange={countryField.onChange}
                />
              )}
            />
          }
          classNames={{
            inputClassName: "pl-3",
            startContentWrapperClassName: "bg-transparent pr-0",
          }}
          placeholder="Enter"
          label={inputDef.name}
          isInvalid={!!errorMessage}
          errorMessage={errorMessage}
          {...field}
        />
      )}
    />
  );
};
